import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, InputAdornment, TextField, TextFieldProps, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { BaseDialog } from '@components/common/BaseDialog';
import { ModalIndex } from '@/common/i-doc/i-doc.registry';
import { dailyNormEditDialog as t } from '@/styles/tokens';

// Спільні елементи модалок редагування денних норм (Figma).

interface NormDialogShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  onSave: () => void;
  saving?: boolean;
  /** Зберегти не можна (напр. сума БЖВ понад 100%). */
  saveDisabled?: boolean;
  iDoc: ModalIndex;
  children: React.ReactNode;
}

const FOOTER_BUTTON_SX = { width: 120, height: 40, px: 2, borderRadius: '6px', fontSize: 14, lineHeight: '20px', fontWeight: 500 } as const;

export const NormDialogShell: React.FC<NormDialogShellProps> = ({ open, onClose, title, onSave, saving, saveDisabled, iDoc, children }) => {
  const { t: tr } = useTranslation();
  return (
  <BaseDialog
    open={open}
    onClose={onClose}
    title={title}
    iDoc={iDoc}
    maxWidth={false}
    paperSx={{ width: 500, maxWidth: 'calc(100% - 32px)', m: 2, borderRadius: '16px', boxShadow: t.shadow }}
    headerSx={{
      p: '16px 24px',
      minHeight: 64,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      '& > .MuiBox-root': { width: '100%' },
      '& .MuiIconButton-root': { width: 32, height: 32, p: 0, ml: '14px', color: t.close },
    }}
    titleSx={{ fontSize: 20, lineHeight: '23px', fontWeight: 600, color: t.text }}
    contentSx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, borderColor: t.divider }}
    actionsSx={{ height: 80, px: 3, py: 0, gap: 1, '& > :not(style) ~ :not(style)': { ml: 0 } }}
    actions={
      <>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ ...FOOTER_BUTTON_SX, bgcolor: 'background.paper', color: t.accent, borderColor: t.accent, '&:hover': { borderColor: t.accentHover, bgcolor: 'background.paper' } }}
        >
          {tr('clientProfile.dailyNorms.edit.cancel')}
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={<SaveIcon />}
          onClick={onSave}
          disabled={saving || saveDisabled}
          sx={{ ...FOOTER_BUTTON_SX, bgcolor: t.accent, '&:hover': { bgcolor: t.accentHover }, '&.Mui-disabled': { bgcolor: t.accentDisabled, color: 'common.white' } }}
        >
          {tr('clientProfile.dailyNorms.edit.save')}
        </Button>
      </>
    }
  >
    {children}
  </BaseDialog>
  );
};

/** Поле з одиницею праворуч (Universal input, 40px). */
export const NormInput: React.FC<{ unit: string } & Pick<TextFieldProps, 'value' | 'onChange' | 'autoFocus' | 'id' | 'sx'>> = ({
  unit,
  sx,
  ...props
}) => (
  <TextField
    {...props}
    type="number"
    InputProps={{
      endAdornment: (
        <InputAdornment position="end" sx={{ ml: 1 }}>
          <Typography sx={{ fontSize: 14, lineHeight: '22px', color: t.textMuted }}>{unit}</Typography>
        </InputAdornment>
      ),
    }}
    sx={{
      '& .MuiOutlinedInput-root': { height: 40, pr: '12px', borderRadius: '6px', bgcolor: 'background.paper', fontSize: 14, lineHeight: '22px', color: t.text },
      '& .MuiOutlinedInput-notchedOutline': { borderColor: t.inputBorder },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.accent },
      // Відступи 12 по краях, 8 між значенням і одиницею.
      '& input': { pl: '12px', pr: 0 },
      '& input[type=number]': { MozAppearance: 'textfield' },
      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', m: 0 },
      ...((sx as object) ?? {}),
    }}
  />
);

export const MutedText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 14, lineHeight: '20px', color: t.textMuted }}>{children}</Typography>
);

/** Сірий блок «Рекомендована норма»: значення, пояснення, кнопка формули. */
export const RecommendedBlock: React.FC<{
  hint?: string;
  /** Кнопки формул (напр. BMR, AMR) - пояснення розрахунку. */
  formulaLabels?: string[];
  onFormulaClick?: (label: string) => void;
  children: React.ReactNode;
}> = ({
  hint,
  formulaLabels = [],
  onFormulaClick,
  children,
}) => {
  const { t: tr } = useTranslation();
  return (
  <Box sx={{ p: '14px', borderRadius: '12px', bgcolor: t.recommendedBg, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
    <MutedText>{tr('clientProfile.dailyNorms.edit.recommended')}</MutedText>
    {children}
    {hint && <MutedText>{hint}</MutedText>}
    {formulaLabels.length > 0 && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {formulaLabels.map((formulaLabel) => (
          <Button
            key={formulaLabel}
            variant="outlined"
            startIcon={<InfoOutlinedIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => onFormulaClick?.(formulaLabel)}
            sx={{
              height: 32,
              px: '12px',
              borderRadius: '6px',
              fontSize: 12,
              lineHeight: '16px',
              fontWeight: 500,
              textTransform: 'uppercase',
              color: t.accent,
              borderColor: t.accent,
              '& .MuiButton-startIcon': { ml: 0, mr: 1 },
              '&:hover': { borderColor: t.accentHover, bgcolor: 'transparent' },
            }}
          >
            {formulaLabel}
          </Button>
        ))}
      </Box>
    )}
  </Box>
  );
};

/** Значення рекомендованої норми (18/600) з необов'язковою іконкою та підписом. */
export const RecommendedValue: React.FC<{ color: string; icon?: React.ReactElement; caption?: string; children: React.ReactNode }> = ({
  color,
  icon,
  caption,
  children,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: icon ? '8px' : '4px', color }}>
    {icon && React.cloneElement(icon, { sx: { fontSize: 20 } })}
    <Typography sx={{ fontSize: 18, lineHeight: '20px', fontWeight: 600, color: 'inherit' }}>{children}</Typography>
    {caption && <Typography sx={{ fontSize: 12, lineHeight: '14px', color: t.text }}>{caption}</Typography>}
  </Box>
);
